/** Page CRUD générique pour les ressources simples (zones, EPI, véhicules, tournées). */

import { useState, type ReactNode } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { Header } from '../components/layout/Header';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';

interface Field<T> {
  key: keyof T & string;
  label: string;
  placeholder?: string;
  required?: boolean;
  /** Type de champ : texte (défaut), heure (HH:MM) ou liste déroulante. */
  type?: 'text' | 'time' | 'select';
  /** Options pour un champ `select`. */
  options?: { value: string; label: string }[];
}

interface Props<T extends { id: number; is_active?: boolean }> {
  title: string;
  itemLabel: string; // ex: "véhicule"
  fields: Field<T>[];
  /** Colonnes à afficher dans la grille (en plus du nom géré nativement). */
  renderExtra?: (item: T) => ReactNode;
  query: UseQueryResult<T[]>;
  createMut: UseMutationResult<T, Error, Record<string, unknown>>;
  updateMut: UseMutationResult<T, Error, { id: number; data: Record<string, unknown> }>;
  deleteMut: UseMutationResult<void, Error, number>;
  /** Icône à afficher dans la pastille de chaque carte. */
  icon: ReactNode;
}

export function SimpleCrudPage<T extends { id: number; is_active?: boolean }>({
  title,
  itemLabel,
  fields,
  renderExtra,
  query,
  createMut,
  updateMut,
  deleteMut,
  icon,
}: Props<T>) {
  const { data, isLoading } = query;
  const [editing, setEditing] = useState<T | null>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<Record<string, string>>({});
  const [error, setError] = useState('');

  const openCreate = () => {
    setEditing(null);
    setForm({});
    setError('');
    setOpen(true);
  };

  const openEdit = (item: T) => {
    setEditing(item);
    const f: Record<string, string> = {};
    for (const fld of fields) {
      const v = (item as Record<string, unknown>)[fld.key];
      f[fld.key] = v == null ? '' : String(v);
    }
    setForm(f);
    setError('');
    setOpen(true);
  };

  const handleSave = async () => {
    setError('');
    for (const fld of fields) {
      if (fld.required && !form[fld.key]?.trim()) {
        setError(`${fld.label} requis`);
        return;
      }
    }
    const payload: Record<string, unknown> = {};
    for (const fld of fields) {
      const val = form[fld.key]?.trim();
      payload[fld.key] = val ? val : null;
    }
    try {
      if (editing) {
        await updateMut.mutateAsync({ id: editing.id, data: payload });
      } else {
        await createMut.mutateAsync(payload);
      }
      setOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    }
  };

  const handleDelete = async (item: T) => {
    if (!window.confirm(`Désactiver ${itemLabel} ?`)) return;
    try {
      await deleteMut.mutateAsync(item.id);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Erreur');
    }
  };

  return (
    <div>
      <Header
        title={title}
        action={
          <Button size="sm" onClick={openCreate}>
            <Plus size={14} className="mr-1" /> Nouveau
          </Button>
        }
      />

      {isLoading ? (
        <Card><p className="text-sm text-stone-400">Chargement...</p></Card>
      ) : !data?.length ? (
        <Card>
          <div className="text-center py-8">
            <p className="text-sm text-stone-500 mb-4">Aucun {itemLabel} configuré</p>
            <Button size="sm" onClick={openCreate}>
              <Plus size={14} className="mr-1" /> Créer le premier
            </Button>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.map((item) => {
            const name = (item as Record<string, unknown>)[fields[0].key];
            return (
              <Card key={item.id} className="hover:border-accent/40 transition-colors">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-accent-soft flex items-center justify-center flex-shrink-0 text-accent">
                      {icon}
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-ink truncate">{String(name ?? '')}</h3>
                      {renderExtra?.(item)}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => openEdit(item)}
                      className="p-1.5 rounded hover:bg-stone-100 text-stone-500 hover:text-ink"
                      title="Modifier"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      onClick={() => handleDelete(item)}
                      className="p-1.5 rounded hover:bg-rose-50 text-stone-500 hover:text-rose-600"
                      title="Désactiver"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={editing ? `Modifier ${itemLabel}` : `Nouveau ${itemLabel}`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>Annuler</Button>
            <Button
              onClick={handleSave}
              loading={createMut.isPending || updateMut.isPending}
            >
              {editing ? 'Enregistrer' : 'Créer'}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          {fields.map((fld) => {
            const label = fld.label + (fld.required ? '' : ' (optionnel)');
            if (fld.type === 'select') {
              return (
                <div key={fld.key} className="space-y-1">
                  <label className="block text-xs font-medium text-gray-600">{label}</label>
                  <select
                    value={form[fld.key] ?? ''}
                    onChange={(e) => setForm({ ...form, [fld.key]: e.target.value })}
                    className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-900
                               focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent"
                  >
                    <option value="">— Aucun —</option>
                    {fld.options?.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              );
            }
            return (
              <Input
                key={fld.key}
                type={fld.type === 'time' ? 'time' : 'text'}
                label={label}
                value={form[fld.key] ?? ''}
                onChange={(e) => setForm({ ...form, [fld.key]: e.target.value })}
                placeholder={fld.placeholder}
                autoFocus={fld === fields[0]}
              />
            );
          })}
          {error && (
            <div className="text-xs bg-red-50 border border-red-200 text-red-700 rounded-md p-2">
              {error}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}
